import { z } from 'zod';
import { S3_CONFIG } from '@/lib/s3/config';
import { UPLOAD_LIMIT_CEILING_BYTES } from '@/lib/validators/platform-settings.schema';

const timezoneEnum = z.enum(['eastern', 'central', 'mountain', 'pacific', 'other']);

const gradeLevelEnum = z.enum([
  'kindergarten',
  '1st',
  '2nd',
  '3rd',
  '4th',
  '5th',
  '6th',
  '7th',
  '8th',
  'middle_school',
  'high_school',
]);

// Broad, evergreen subject areas rather than specific course names — courses
// come and go, but a parent may still want to ask about a subject we aren't
// currently offering. Stored in the `interested_courses` jsonb column.
const interestedSubjectEnum = z.enum([
  'reading',
  'writing',
  'public_speaking',
  'math',
  'technology',
  '3d_modeling',
  'esl',
  'world_languages',
]);

export const contactFormSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(100),
  childName: z.string().trim().max(100).optional(),
  email: z.string().trim().toLowerCase().email('Invalid email address').max(254),
  phone: z
    .string()
    .trim()
    .max(20)
    .regex(/^[+\d\s\-()]+$/, 'Invalid phone number format')
    .optional()
    .or(z.literal('')),
  timezone: timezoneEnum.optional(),
  gradeLevel: gradeLevelEnum.optional(),
  interestedCourses: z.array(interestedSubjectEnum).max(8).optional(),
  goals: z.string().trim().max(1000).optional(),
  additionalInfo: z.string().trim().max(1000).optional(),
  // ── Teacher-inquiry contact mode ──
  // Discriminant threaded from the form `variant`. Optional (absent ⇒ parent,
  // handled in createLead) rather than a discriminated union so name/email/
  // consent stay required for both modes and parent churn stays minimal.
  inquiryType: z.enum(['parent', 'teacher']).optional(),
  // Years of experience: a blank field must stay `undefined`, NOT coerce to 0.
  // Outer `.optional()` keeps the key optional in the z.input type (the form's
  // value type) so react-hook-form's resolver inference stays clean.
  yearsOfExperience: z
    .preprocess(
      (v) => (v === '' || v == null ? undefined : v),
      z.coerce.number().int().min(0).max(80).optional()
    )
    .optional(),
  // Supporting documents (resume/certs/transcripts). Each carries an opaque,
  // server-generated S3 key (re-validated + HeadObject'd server-side) and the
  // original filename. Up to 5 files. Subjects reuse `goals`; Message reuses
  // `additionalInfo`.
  credentialDocuments: z
    .array(
      z.object({
        s3Key: z
          .string()
          .trim()
          .max(300)
          .regex(/^leads\/teacher-credentials\//, 'Invalid key')
          .refine((k) => !k.includes('..') && !k.includes('%'), 'Invalid key'),
        filename: z.string().trim().min(1).max(255),
      })
    )
    .max(5)
    .optional(),
  honeypot: z.string().max(0).optional(),
  consentAcknowledged: z.literal(true, {
    message: 'You must consent to continue',
  }),
});

/**
 * Teacher-inquiry contact mode — anonymous presigned-upload request body for
 * `POST /api/leads/credential-upload`. The contentType/size are re-validated
 * against the allow-list + cap server-side before any URL is signed.
 */
export const credentialUploadRequestSchema = z.object({
  filename: z.string().trim().min(1).max(255),
  contentType: z.string().trim().max(150),
  fileSizeBytes: z
    .number()
    .int()
    .positive()
    .max(UPLOAD_LIMIT_CEILING_BYTES),
});

/**
 * Opt-in lead payload from the visitor chat widget ("Sunny"). Reuses the same
 * grade/subject enums as the contact form. `consentAcknowledged` MUST be the
 * literal `true` (AC7) — capture is rejected otherwise. No honeypot/inquiryType
 * (the chatbot derives `source:'chatbot'` server-side).
 */
export const chatLeadSchema = z.object({
  conversationId: z.string().uuid(),
  name: z.string().trim().min(1, 'Name is required').max(100),
  email: z.string().trim().toLowerCase().email('Invalid email address').max(254),
  childName: z.string().trim().max(100).optional(),
  gradeLevel: gradeLevelEnum.optional(),
  interestedCourses: z.array(interestedSubjectEnum).max(8).optional(),
  goals: z.string().trim().max(1000).optional(),
  additionalInfo: z.string().trim().max(1000).optional(),
  consentAcknowledged: z.literal(true, {
    message: 'You must consent to be contacted',
  }),
});
export type ChatLeadInput = z.infer<typeof chatLeadSchema>;

export const newsletterSchema = z.object({
  email: z.string().trim().toLowerCase().email('Invalid email address').max(254),
  honeypot: z.string().max(0).optional(),
});

export type ContactFormInput = z.infer<typeof contactFormSchema>;
// Pre-transform value shape — what react-hook-form holds in form state (e.g.
// `yearsOfExperience` is a raw string from the number input before coercion).
export type ContactFormValues = z.input<typeof contactFormSchema>;
export type NewsletterInput = z.infer<typeof newsletterSchema>;
export type CredentialUploadRequestInput = z.infer<typeof credentialUploadRequestSchema>;
